<app-info-drawer>
  <div drawer class="flex flex-col gap-3">
    <app-contacts-dropdown
      #salesmanDropdown
      [title]="'salesman' | translate"
      [controlName]="'salesmanUid'"
      createKey="Salesman"
      [parentForm]="form"
      [filter]="salesmanFilter"
    >
      @if (form.controls.salesmanUid.errors?.required && showValidation) {
        <kendo-formerror>{{
          "salesmanIsRequired" | translate
        }}</kendo-formerror>
      }
    </app-contacts-dropdown>
    <app-date-calendar
      [title]="'expectedDate' | translate"
      [parentForm]="form"
      controlName="expectedDate"
    >
    </app-date-calendar>
    <app-stock-locations-dropdown
      [parentForm]="form"
      controlName="locationUid"
      title="{{ 'warehouse' | translate }}"
    >
    </app-stock-locations-dropdown>
    <div [formGroup]="form">
      <kendo-formfield class="mt-n2">
        <kendo-label [text]="'lpoReference' | translate">
          <kendo-textbox formControlName="lpo"></kendo-textbox>
        </kendo-label>
      </kendo-formfield>
      <kendo-formfield class="mt-n2">
        <kendo-label [text]="'internalNotes' | translate">
          <kendo-textarea formControlName="internalNotes" [rows]="2"></kendo-textarea>
        </kendo-label>
      </kendo-formfield>
    </div>
    @if (allowMultiCurrency) {
      <app-currency-dropdown
        class="mt-n2"
        [parentForm]="form"
        controlName="currencyUid"
        [loadDefaultValue]="true"
        settingKey="defaultSellingCurrency"
      >
        @if (form.controls.currencyUid.errors?.required && showValidation) {
          <kendo-formerror>{{
            "currencyIsRequired" | translate
          }}</kendo-formerror>
        }
      </app-currency-dropdown>
    }
    <!-- Related to Currency Dropdown, to fetch the latest rate, based on selected currency -->
    <app-currency-rate-input
      [hidden]="true"
      [parentForm]="form"
    ></app-currency-rate-input>
    @if (isCompanyProjectLinked) {
      <app-company-project-dropdown
        class="mt-n2"
        [parentForm]="form"
        controlName="projectUid"
      ></app-company-project-dropdown>
    }
  </div>
  <main drawerContent>
    <div class="flex justify-end items-center sub-header-container">
      <div class="sub-header-actions">
        <kendo-buttongroup class="status-group" [disabled]="true">
          <button
            kendoButton
            [toggleable]="true"
            [disabled]="true"
            class="selected-status-button"
          >
            {{ "draft" | translate }}
          </button>
          <button kendoButton [toggleable]="true" [disabled]="true">
            {{ "confirmed" | translate }}
          </button>
          <button kendoButton [toggleable]="true" [disabled]="true">
            {{ "closed" | translate }}
          </button>
        </kendo-buttongroup>
      </div>
    </div>

    <div class="content-container viewport-fill">
      @if (!isDrawerOpened) {
        <app-error-form
          [errorMessage]="errorMessage"
          [validationErrorMessages]="validationErrorMessages"
        ></app-error-form>
      }

      <div class="flex flex-col gap-6">
        <kendo-card>
          <form [formGroup]="form">
            <div class="flex justify-between items-center mb-3 px-2">
              <h2 class="m-0">
                {{ "addSalesOrder" | translate }}
              </h2>
              <app-actions-dropdown
                [data]="actionMenuOptions"
                (itemClick)="openCurrencyInfoAddDrawer()"
              ></app-actions-dropdown>
            </div>
            <section class="flex flex-col gap-6 view-section-header-container">
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
                <app-contacts-dropdown
                  [parentForm]="form"
                  [title]="('customer' | translate) + '*'"
                  [controlName]="'contact'"
                  createKey="Customer"
                  [filter]="customersFilter"
                  [valuePrimitive]="false"
                  (customerSelect)="sendCustomerToEditContact($event)"
                >
                  @if (
                    form.controls.contact.errors?.required && showValidation
                  ) {
                    <kendo-formerror>{{
                      "customerIsRequired" | translate
                    }}</kendo-formerror>
                  }
                </app-contacts-dropdown>
                <app-address-dropdown
                  [parentForm]="form"
                  [controlName]="'shippingAddressUid'"
                  [title]="'shippingAddress' | translate"
                  [data]="shippingAddresses"
                  [contactUid]="form.controls.contact.value?.uid"
                  [isLoadingAddresses]="isLoadingAddresses"
                  (reloadAddresses)="getContactAddresses(form.controls.contact.value?.uid)"
                ></app-address-dropdown>
                <app-address-dropdown
                  [parentForm]="form"
                  [controlName]="'billingAddressUid'"
                  [title]="'billingAddress' | translate"
                  [data]="billingAddresses"
                  [contactUid]="form.controls.contact.value?.uid"
                  [isLoadingAddresses]="isLoadingAddresses"
                  (reloadAddresses)="getContactAddresses(form.controls.contact.value?.uid)"
                ></app-address-dropdown>
                <app-date-calendar
                  [title]="('date' | translate) + '*'"
                  [parentForm]="form"
                  [controlName]="getName(form.controls.date)"
                  [showValidation]="showValidation"
                  (valueChange)="onDateChange($event)"
                >
                </app-date-calendar>

                <app-payment-terms-dropdown
                  #paymentTermsDropdown
                  [controlName]="'paymentTermUid'"
                  [title]="'paymentTerm' | translate"
                  [parentForm]="form"
                ></app-payment-terms-dropdown>
                <!-- ZEY-2215: the order note is customer-facing — it is carried onto the sales invoice
                     and printed there (driver / customer instructions), so it sits on the form itself
                     rather than in the side drawer. Internal Notes stays in the drawer: staff-only. -->
                <app-textarea-input
                  [label]="'notes' | translate"
                  [parentForm]="form"
                  controlName="notes"
                ></app-textarea-input>
              </div>

              <div>
                <app-editing-contact
                  [contactEvent]="sendCustomer.asObservable()"
                  (sendSalesman)="onSalesmanReceived($event)"
                ></app-editing-contact>
              </div>
            </section>
          </form>
        </kendo-card>
        <kendo-card>
          <ng-container
            *ngTemplateOutlet="orderEditableGrid?.searchProductBtnTemplate"
          ></ng-container>
          <kendo-tabstrip class="mt-0">
            <kendo-tabstrip-tab
              title="{{ 'orderItems' | translate }}"
              [selected]="true"
            >
              <ng-template kendoTabContent>
                <!-- <label for="switch">Tax Exempt</label>
                      <kendo-switch
                        #switch
                        (valueChange)="onTaxExemptValueChange($event)"
                        formControlName="isTaxExempt"
                      ></kendo-switch> -->
                <app-order-editable-grid
                  #orderEditableGrid
                  [showNewPrint]="printPlatesActive"
                  [parentForm]="form"
                  [showNoteFields]="true"
                  orderType="Sales"
                  orderStatusType="Draft"
                  (checkDirty)="dirtyFormHandler($event)"
                ></app-order-editable-grid>
              </ng-template>
            </kendo-tabstrip-tab>
          </kendo-tabstrip>
        </kendo-card>
        @if (form.value.contact?.uid) {
          <app-customer-frequent-invoiced-products-grid
            [customerUid]="form.value.contact?.uid"
            (addRecord)="addLineItem($event)"
          ></app-customer-frequent-invoiced-products-grid>
        }
      </div>
    </div>

    <app-action-bar
      [isBoxShadow]="false"
      [isWaiting]="isWaiting"
      [screenType]="'Form'"
      [actionsOptions]="[
        {
          visible: true,
          class: 'btn-primary',
          label: 'Save and Close',
          labelName: ('saveAndClose' | translate),
          options: [{ text: ('saveAndNew' | translate), value: 'Save and New' }]
        },
        { visible: true, class: 'btn-outline-secondary', label: 'Discard', labelName: ('discard' | translate) }
      ]"
      [recordsOptions]="[
        {
          visible: true,
          class: 'btn-outline-primary',
          label: 'Confirm',
          labelName: ('confirm' | translate)
        },
        {
          visible: allowDeliverandInvoice,
          class: 'btn-outline-primary',
          label: 'Deliver and Invoice',
          labelName: ('deliverAndInvoice' | translate)
        }
      ]"
      (buttonClick)="onActionClick($event, 'add')"
    >
    </app-action-bar>
  </main>
</app-info-drawer>
